import { isAbpHttpError, UnauthorizedHttpError } from '../../platform/abp/httpClient'

const UNREACHABLE =
  /failed to fetch|networkerror|network error|load failed|econnrefused|connection refused|not connected|err_connection|websocket|failed to start the connection|failed to complete negotiation|connection was stopped/i

/** Full copy — map / boot (primary). Keep right-rail shorter via {@link STUDIO_UNREACHABLE_RAIL}. */
export const STUDIO_UNREACHABLE_MESSAGE =
  "Couldn't reach the server right now. Scenario Studio will open once the connection is restored."

/** Short right-rail echo — map banner / boot card owns the full sentence. */
export const STUDIO_UNREACHABLE_RAIL = "Couldn't reach the server."

/** Mid-session veil after reconnect gave up — title + supporting line. */
export const STUDIO_LINK_DOWN_TITLE = "Couldn't reach the server"
export const STUDIO_LINK_DOWN_HINT = 'Check your connection, then try again.'

/** The host never answered, or the connection died before a response. */
export function isStudioUnreachable(err: unknown): boolean {
  if (err instanceof UnauthorizedHttpError) return false
  if (isAbpHttpError(err)) return err.httpStatus === 0 || err.httpStatus >= 502
  if (err instanceof TypeError) return true
  const message = err instanceof Error ? err.message : ''
  return UNREACHABLE.test(message)
}

/** True when UI text is the unreachable primary (or its short rail echo). */
export function isStudioUnreachableNotice(message: string | null | undefined): boolean {
  if (!message) return false
  return (
    message === STUDIO_UNREACHABLE_MESSAGE ||
    message === STUDIO_UNREACHABLE_RAIL ||
    message === STUDIO_LINK_DOWN_TITLE ||
    /^Couldn'?t reach the server/i.test(message) ||
    /^Can't reach the server/i.test(message)
  )
}

function looksTechnical(text: string): boolean {
  return /exception|stack trace|failed to fetch|status code|request failed \(|signalr|websocket/i.test(text)
}

/** What to say when Scenario Studio itself cannot load. */
export function studioLoadMessage(err: unknown): string {
  if (err instanceof UnauthorizedHttpError) {
    return 'Your session has expired. Sign in again, then try again.'
  }
  if (isStudioUnreachable(err)) {
    return STUDIO_UNREACHABLE_MESSAGE
  }
  if (isAbpHttpError(err) && err.httpStatus >= 500) {
    return 'The server ran into a problem. Try again in a moment.'
  }
  if (isAbpHttpError(err)) {
    const text = err.message.trim()
    if (text && !looksTechnical(text)) return text
  }
  return "Scenario Studio couldn't load. Try again in a moment."
}

/** Action failures keep their own sentence, unless the server never answered. */
export function studioActionMessage(err: unknown, fallback: string): string {
  if (
    err instanceof UnauthorizedHttpError ||
    isStudioUnreachable(err) ||
    (isAbpHttpError(err) && err.httpStatus >= 500)
  ) {
    return studioLoadMessage(err)
  }
  if (isAbpHttpError(err)) {
    const text = err.message.trim()
    if (text && !looksTechnical(text)) return text
  }
  return fallback
}
